// src/App.tsx

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { lazy, Suspense } from 'react';
import { Route } from 'react-router';
import { BrowserRouter, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import CustomErrorBoundary from './components/common/CustomErrorBoundary';
import { ScrollToTop } from './components/common/ScrollToTop';
import useAuthRedirect from './hooks/useAuthRedirect';
import AppLayout from './layout/AppLayout';

// 로딩 컴포넌트
const PageLoader = () => (
  <div className='flex items-center justify-center min-h-screen'>
    <div className='w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin' />
  </div>
);

// Lazy Loading으로 페이지 컴포넌트 import
// Dashboard
const Home = lazy(() => import('./pages/Dashboard/Home'));

// Auth Pages
const SignIn = lazy(() => import('./pages/AuthPages/SignIn'));
const SignUp = lazy(() => import('./pages/AuthPages/SignUp'));
const SignOut = lazy(() => import('./pages/AuthPages/SignOut'));

// User & Profile
const UserProfiles = lazy(() => import('./pages/UserProfiles'));

// Calendar & Blank
const Calendar = lazy(() => import('./pages/Calendar'));
const Blank = lazy(() => import('./pages/Blank'));

// Forms
const FormElements = lazy(() => import('./pages/Forms/FormElements'));

// Tables (Dealer, Point 등)
const BasicTables = lazy(() => import('./pages/Tables/BasicTables'));
const DealerDetailPage = lazy(() => import('./pages/DealerDetailPage'));
const DealerLocationPage = lazy(() => import('./pages/DealerLocationPage'));
const SystemInfoPage = lazy(() => import('./pages/SystemInfoPage'));

// UI Elements
const Alerts = lazy(() => import('./pages/UiElements/Alerts'));
const Avatars = lazy(() => import('./pages/UiElements/Avatars'));
const Badges = lazy(() => import('./pages/UiElements/Badges'));
const Buttons = lazy(() => import('./pages/UiElements/Buttons'));
const Images = lazy(() => import('./pages/UiElements/Images'));
const Videos = lazy(() => import('./pages/UiElements/Videos'));

// Charts
const LineChart = lazy(() => import('./pages/Charts/LineChart'));
const BarChart = lazy(() => import('./pages/Charts/BarChart'));

// Error Pages
const NotFound = lazy(() => import('./pages/OtherPage/NotFound'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1분
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// 내부 컴포넌트로 라우팅 로직 분리
const AppRoutes = () => {
  useAuthRedirect(); // JWT 체크 및 자동 리다이렉트

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Dashboard Layout */}
        <Route element={<AppLayout />}>
          <Route index path='/' element={<Home />} />

          {/* Others Page */}
          <Route path='/profile' element={<UserProfiles />} />
          <Route path='/calendar' element={<Calendar />} />
          <Route path='/blank' element={<Blank />} />

          {/* Forms */}
          <Route path='/form-elements' element={<FormElements />} />

          {/* Tables */}
          <Route path='/dealer' element={<BasicTables />} />
          <Route path='/dealers/:id' element={<DealerDetailPage />} />
          <Route path='/dealer-location' element={<DealerLocationPage />} />
          <Route path='/point' element={<BasicTables />} />
          <Route path='/schedule' element={<BasicTables />} />
          <Route path='/review' element={<BasicTables />} />
          <Route path='/card' element={<BasicTables />} />
          <Route path='/payment' element={<BasicTables />} />
          <Route path='/notice' element={<BasicTables />} />
          <Route path='/faq' element={<BasicTables />} />
          <Route path='/settings' element={<BasicTables />} />
          <Route path='/service' element={<BasicTables />} />
          <Route path='/product' element={<BasicTables />} />
          <Route path='/report' element={<BasicTables />} />
          <Route path='/activity-log' element={<BasicTables />} />
          <Route path='/system-info' element={<SystemInfoPage />} />

          {/* UI Elements */}
          <Route path='/alerts' element={<Alerts />} />
          <Route path='/avatars' element={<Avatars />} />
          <Route path='/badge' element={<Badges />} />
          <Route path='/buttons' element={<Buttons />} />
          <Route path='/images' element={<Images />} />
          <Route path='/videos' element={<Videos />} />

          {/* Charts */}
          <Route path='/line-chart' element={<LineChart />} />
          <Route path='/bar-chart' element={<BarChart />} />
        </Route>

        {/* Auth Layout */}
        <Route path='/signin' element={<SignIn />} />
        <Route path='/signup' element={<SignUp />} />
        <Route path='/signout' element={<SignOut />} />

        {/* Fallback Route */}
        <Route path='*' element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}

      {process.env.NODE_ENV === 'development' && (
        <div className='fixed bottom-4 left-4 z-[9999] bg-black text-white px-3 py-2 rounded-lg font-mono text-xs'>
          <span className='sm:hidden'>XS</span>
          <span className='hidden sm:block md:hidden'>SM (≥640px)</span>
          <span className='hidden md:block lg:hidden'>MD (≥768px)</span>
          <span className='hidden lg:block xl:hidden'>LG (≥1024px)</span>
          <span className='hidden xl:block 2xl:hidden'>XL (≥1280px)</span>
          <span className='hidden 2xl:block'>2XL (≥1536px)</span>
        </div>
      )}

      <BrowserRouter>
        <CustomErrorBoundary showDetails>
          <ScrollToTop />
          <AppRoutes />
        </CustomErrorBoundary>
      </BrowserRouter>

      <Toaster position='top-center' richColors closeButton duration={4000} expand={false} theme='system' />
    </QueryClientProvider>
  );
}
